import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readJsonPref, writeJsonPref } from '@utils/localJsonPref';

export type ActivityView = 'list' | 'tracker' | 'workplan';

const PARAM_VIEW = 'view';
const PREF_KEY = 'dorca.activities.view';

/** The view a first visit opens on — the first tab — and the one that never
 *  needs the `?view=` param. */
const DEFAULT_VIEW: ActivityView = 'workplan';

/** The views a `?view=` param (or the stored pref) may name. */
const NAMED_VIEWS: ReadonlySet<string> = new Set<ActivityView>(['list', 'tracker']);

function asView(value: unknown): ActivityView | null {
  if (value === DEFAULT_VIEW) return DEFAULT_VIEW;
  return typeof value === 'string' && NAMED_VIEWS.has(value) ? (value as ActivityView) : null;
}

/** The last-used view. Leaving Activities and returning via the sidebar drops the
 *  `?view=` URL param, so without this the page would snap back to the default. */
function readViewPref(): ActivityView {
  return asView(readJsonPref(PREF_KEY)) ?? DEFAULT_VIEW;
}

export function useActivityView(): { view: ActivityView; setView: (v: ActivityView) => void } {
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL param is the source of truth when present (shareable / back-button);
  // otherwise fall back to the persisted last-used view.
  const view: ActivityView = asView(searchParams.get(PARAM_VIEW)) ?? readViewPref();

  const setView = useCallback(
    (next: ActivityView) => {
      writeJsonPref(PREF_KEY, next);
      // Deliberately URGENT (no startTransition): the product decision is that
      // clicking the toggle switches views immediately — the incoming view
      // owns its own not-ready state (the delay-gated loading card) rather
      // than the old view lingering while the new one prepares.
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next === DEFAULT_VIEW) sp.delete(PARAM_VIEW);
          else sp.set(PARAM_VIEW, next);
          return sp;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { view, setView };
}
